import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  Label,
  InputText,
  InputDate,
  InputSelect,
  Switch,
  PersonAvatar,
  IconButton,
} from '@teo/components';
import { CrossFilled } from '@teo/components/icons';
import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { PaginationMultiSelect } from '@/components/PaginationMultiSelect';
import { useGet } from '@/query/fetchHooks';
import { InputSelectOption } from '@/models/organisation.interface';
import { axios } from '@/axios';
import { config } from '@/config';
import { emailRegex } from '@/constants/regex.constants';
import { newUid } from '@/util';
import { languages } from '@/constants/languages';

const autoReportFrequency = [
  {
    value: '86400',
    label: 'Daily',
  },
  {
    value: '604800',
    label: 'Weekly',
  },
  {
    value: '2592000',
    label: 'Monthly',
  },
  {
    value: '31536000',
    label: 'Yearly',
  },
];

const AutoReportModal = ({
  setIsOpenAutoReport,
  closeModal,
  user,
  reportTypeList,
  setRefetchTable,
}: any) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const [emailValue, setEmailValue] = useState(
    auth?.user?.email ? auth?.user?.email : ''
  );
  const [sentEmails, setSentEmails] = useState<any>([]);
  const [reportName, setReportName] = useState('');
  const [lpGroupsAutoReport, setLpGroupsAutoReport] = useState<any>([]);
  const [selectedLpGroups, setSelectedLpGroups] = useState<any[]>([]);
  const [fromDateAuto, setFromDateAuto] = useState<Date | undefined>();
  const [frequency, setFrequency] = useState({
    label: autoReportFrequency[2].label,
    value: autoReportFrequency[2].value,
  });
  const [autoReportEmail, setAutoReportEmail] = useState<boolean>(true);

  const [addEamilValid, setAddEamilValid] = useState(true);

  const [reportType, setReportType] = useState({
    label: reportTypeList[0].label,
    value: reportTypeList[0].value,
  });

  const [language, setLanguage] = useState(
    languages?.find((option) => option.value === auth?.user?.lang)
  );

  const rawReportType = reportType?.value;

  const overviewLp =
    rawReportType === 'overviewLp' ||
    rawReportType === 'overviewLpNoCompetences';

  const noCompetences =
    rawReportType === 'lpOverviewNoCompetences' ||
    rawReportType === 'overviewLpNoCompetences';

  const backendFormType =
    rawReportType === 'overviewLp' ||
    rawReportType === 'lpOverviewNoCompetences' ||
    rawReportType === 'overviewLpNoCompetences'
      ? 'lpOverview'
      : rawReportType;

  const handleAddUsers = async (newValue: any) => {
    const addLpGroup = [...lpGroupsAutoReport];
    for (const obj2 of newValue) {
      const objID = obj2?.id || obj2?.courseId;
      if (objID) {
        const existingCourses = lpGroupsAutoReport.find((obj1: any) =>
          obj1?.id
            ? obj1?.id === objID
            : obj1?.courseId && obj1?.courseId === objID
        );

        if (!existingCourses) {
          addLpGroup.push(obj2);
        }
      }
    }
    setLpGroupsAutoReport(addLpGroup);
    setSelectedLpGroups([]);
  };
  const handleRemove = (id: any) => {
    const removeCourse = lpGroupsAutoReport?.filter((item: any) => {
      const itemId = item?.id || item?.courseId;
      return itemId !== id;
    });
    setLpGroupsAutoReport(removeCourse);
  };

  useEffect(() => {
    if (emailValue) {
      const emails = emailValue.split(',').map((email) => email.trim());
      const isValidEmail = (email: any) => {
        return emailRegex.test(email);
      };
      const allEmailsValid = emails.every(isValidEmail);
      if (allEmailsValid) {
        setAddEamilValid(false);
      } else {
        setAddEamilValid(true);
      }
    }
  }, [emailValue]);

  const handleAddEmails = () => {
    if (!addEamilValid) {
      const emails = emailValue.split(',').map((email) => email.trim());
      const uniqueEmails = new Set(sentEmails.concat(emails));
      const result = Array.from(uniqueEmails);
      setSentEmails(result);
      setEmailValue('');
      setAddEamilValid(true);
    }
  };

  const handleRemoveEmail = (index: any) => {
    const removeTeam = sentEmails?.filter((el: any, i: any) => index != i);
    setSentEmails(removeTeam);
  };

  const sentReport = () => {
    const promises: any = [];
    promises.push(
      axios.post(`${config.backend}/autoreports`, {
        name:
          reportName || lpGroupsAutoReport.map((x: any) => x.name).join(', '),
        email: [...new Set(sentEmails.concat(emailValue))].map((x) => x),
        organisationId: user?.activeOrganisationId,
        lang: language?.value ? language?.value : user?.lang,
        lpIds: lpGroupsAutoReport?.map((el: any) => el.id || el?.courseId),
        formType: backendFormType,
        startDate: fromDateAuto ? fromDateAuto : null,
        interval: parseInt(frequency?.value, 10),
        noBirth: overviewLp,
        noDiploma: overviewLp,
        noNodes: overviewLp,
        noCompetences,
        username: auth?.user.username,
      })
    );

    Promise.allSettled(promises).then(() => {
      setRefetchTable(newUid(20));
      setTimeout(() => {
        document.body.style.overflow = 'unset';
        setIsOpenAutoReport(false);
      }, 100);
    });
  };

  return (
    <div className="bg-white">
      <div className="fixed inset-0 z-[100] flex rounded-xl bg-white/75 px-3">
        <div className="mx-auto my-8 h-max w-full max-w-[600px]  rounded-xl bg-white drop-shadow-m">
          <Modal.Header onClose={closeModal}>
            {t('pages.report_page.teams_tab.title_auto_modal')}
          </Modal.Header>
          <div className="max-h-[calc(100vh_-_200px)] overflow-auto">
            <div className="p-6">
              <h1 className="text-lg font-semibold">
                {t('pages.report_page.teams_tab.auto_modal_heading')}
              </h1>
              <div className="flex flex-col">
                <div className="mb-1 mt-5 text-sm font-semibold text-black">
                  <Label htmlFor="name-autoreport">
                    {t('pages.report_page.teams_tab.report_name')}
                  </Label>
                  <InputText
                    id="name-autoreport"
                    onChange={(e) => setReportName(e.currentTarget.value)}
                    value={reportName}
                    className="w-full"
                  />
                </div>
                <div className="z-[1] mb-1 mt-4 text-sm font-semibold text-black">
                  <p className="mb-1">{t(`columns.learningpath`)}</p>
                  <div className="flex">
                    <span className="w-full">
                      <PaginationMultiSelect
                        id="users-report"
                        value={selectedLpGroups}
                        onChange={(value: any) => {
                          setSelectedLpGroups(value);
                        }}
                        keyFn={(row: any) => row?.lpgroupId}
                        fetch={useGet.bind(
                          null,
                          `/memberlpgroupdetails?organisationId=${auth?.user?.activeOrganisationId}`
                        )}
                        defaultSort={[{ id: 'lpgroupName', desc: false }]}
                        render={(row: any) => {
                          return row?.lpgroupName
                            ? row?.lpgroupName
                            : row?.lpName;
                        }}
                      />
                    </span>
                    <Button
                      className="ml-3 h-[36px] text-base font-medium"
                      variant="primary"
                      disabled={selectedLpGroups.length === 0}
                      onClick={async () => {
                        handleAddUsers(selectedLpGroups);
                      }}
                    >
                      {t('button.add_btn')}
                    </Button>
                  </div>
                  {lpGroupsAutoReport?.length > 0 && (
                    <div className="mt-6">
                      {lpGroupsAutoReport?.map((lpGroup: any) => (
                        <div
                          key={
                            lpGroup.lpgroupId == null
                              ? lpGroup.lpgroupName
                              : lpGroup.lpgroupId
                          }
                          className="mb-4 flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                              <img
                                src={
                                  lpGroup?.lpgroupPicture ||
                                  lpGroup?.lpPicture ||
                                  '/assets/images/LearnPath.png'
                                }
                                alt={
                                  lpGroup?.lpgroupName || lpGroup?.lpName || ''
                                }
                                className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                              />
                            </div>
                            {lpGroup?.lpgroupName || lpGroup?.lpName}
                          </div>
                          <div
                            data-testid="daleteItem"
                            className="my-auto ml-6"
                          >
                            <IconButton
                              Icon={CrossFilled}
                              variant="ghost"
                              onClick={() =>
                                handleRemove(lpGroup.id || lpGroup.lpgroupId)
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-1 mt-4 min-w-full max-w-[300px] text-sm font-semibold text-black">
                  <p className="mb-1">
                    {t('pages.report_page.teams_tab.type_of_report')}
                  </p>
                  <div className="font-normal">
                    <InputSelect<InputSelectOption>
                      value={reportType}
                      onChange={(e: InputSelectOption) => setReportType(e)}
                      options={reportTypeList}
                    >
                      {(options) => (
                        <span>{options.label && t(options.label)}</span>
                      )}
                    </InputSelect>
                  </div>
                </div>

                <div className="mb-1 mt-4 text-sm font-semibold text-black sm:w-1/2">
                  <Label htmlFor="dateAutoFrom">
                    {t('pages.report_page.teams_tab.reports_from')}
                  </Label>
                  <InputDate
                    id="dateAutoFrom"
                    onChange={(date) => setFromDateAuto(date)}
                    value={fromDateAuto}
                    variant="date"
                  />
                </div>
                <div className="mb-1 mt-4 text-sm font-semibold text-black sm:w-1/2">
                  <Label htmlFor="frequency">
                    {t('pages.report_page.teams_tab.frequency')}
                  </Label>
                  <InputSelect<InputSelectOption>
                    value={frequency}
                    onChange={(e: InputSelectOption) => setFrequency(e)}
                    options={autoReportFrequency}
                  >
                    {(options) => <span>{options.label && options.label}</span>}
                  </InputSelect>
                </div>
                <div className="mb-1 mt-4 text-sm font-semibold text-black sm:w-1/2">
                  <Label htmlFor="language">
                    {t('pages.profile_account.language')}
                  </Label>
                  <div id="language">
                    <InputSelect<InputSelectOption>
                      value={language}
                      onChange={async (lang) => {
                        setLanguage(lang);
                      }}
                      options={languages}
                    >
                      {(options) => t(options.label)}
                    </InputSelect>
                  </div>
                </div>
                <p className="pt-6 text-base font-semibold text-black">
                  {t('pages.report_page.teams_tab.email_reports')}
                </p>
                <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
                  <div data-testid="autoReportEmail">
                    <Switch
                      checked={autoReportEmail}
                      onChange={(isCheck) => setAutoReportEmail(isCheck)}
                    />
                  </div>
                  <div>
                    <h4 className="pb-2 font-normal">
                      {t(
                        'pages.report_page.teams_tab.toggle_email_auto_heading'
                      )}
                    </h4>
                    <p className="text-xs text-grey-05">
                      {t('pages.report_page.teams_tab.toggle_email_auto_text')}
                    </p>
                  </div>
                </div>
                <div className="mb-1 mt-4 text-sm font-semibold text-black">
                  <Label htmlFor="sent_email">
                    {t('pages.report_page.teams_tab.sent_email')}
                  </Label>
                  <InputText
                    id="sent_email"
                    onChange={(e) => setEmailValue(e.currentTarget.value)}
                    value={emailValue}
                    className="w-full"
                  />
                </div>
                <div className="flex pt-6">
                  <Button
                    variant="outline"
                    onClick={handleAddEmails}
                    disabled={addEamilValid}
                  >
                    {t('pages.report_page.teams_tab.add_email')}
                  </Button>
                </div>
                {sentEmails?.length > 0 && (
                  <div className="mt-6">
                    {sentEmails?.map((email: any, index: any) => (
                      <div
                        key={index + email}
                        className="mb-1 flex items-center justify-between"
                      >
                        <p className="text-[12px] font-medium text-[#231f20] sm:text-sm">
                          {email}
                        </p>
                        <div data-testid="daleteItem" className="my-auto ml-6">
                          <IconButton
                            Icon={CrossFilled}
                            variant="ghost"
                            onClick={() => handleRemoveEmail(index)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-solid border-grey-02">
            <Modal.Footer
              cancelButtonLabel={t('pages.manage_tags.cancel_button')}
              onCancel={closeModal}
            >
              <div className="flex gap-3">
                <Button
                  type="button"
                  disabled={lpGroupsAutoReport?.length < 1}
                  onClick={() => sentReport()}
                >
                  {t('pages.report_page.teams_tab.btn_add_auto_report')}
                </Button>
              </div>
            </Modal.Footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoReportModal;
